import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  query,
  orderBy,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  where,
  Timestamp,
  serverTimestamp
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  getMetadata,
  UploadMetadata,
  StorageReference
} from '@angular/fire/storage';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ImagePreview } from './news-image/news-image.component';
import { formatBytes } from '../../shared/utils/format.utils';
import { CompanyRef, CurrentUserService } from '../../shared/services/current-user.service';

export interface NewsImage {
  readonly storagePath: string;
  readonly altText?: string;
  readonly caption?: string;
  readonly order: number;
  readonly uploadedAt: Timestamp;
}

export interface News extends CompanyRef {
  readonly id?: string;
  title: string;
  content: string;
  summary?: string;
  author: string;
  authorId: string;
  newsImages: readonly NewsImage[];
  published: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  publishedAt: Timestamp | null;
  tags: readonly string[];
  sendNotification: boolean;
  viewCount: number;
  featuredImageIndex?: number;
}

type CompanyFields = keyof CompanyRef;
type CreateNewsInput = Omit<News, 'id' | 'createdAt' | 'updatedAt' | 'publishedAt' | 'newsImages' | 'viewCount' | CompanyFields>;
type UpdateNewsInput = Partial<Omit<News, 'id' | 'createdAt' | 'author' | 'authorId' | CompanyFields>>;

interface ImageMetadata {
  url: string;
  size: number;
  contentType: string;
}

const NEWS_COLLECTION = 'news';

@Injectable({
  providedIn: 'root'
})
export class NewsService {
  readonly #firestore = inject(Firestore);
  readonly #storage = inject(Storage);
  readonly #currentUser = inject(CurrentUserService);

  #collection() {
    return collection(this.#firestore, NEWS_COLLECTION);
  }

  #docRef(id: string) {
    return doc(this.#firestore, NEWS_COLLECTION, id);
  }

  #storageRef(path: string): StorageReference {
    return ref(this.#storage, path);
  }

  #getStoragePath(newsId: string, filename: string, timestamp = Date.now()): string {
    const sanitized = filename.replace(/[^a-zA-Z0-9.-]/g, '_');
    return `news/${newsId}/${timestamp}_${sanitized}`;
  }

  // Image Preview Conversion

  mapToImagePreviews(imagesWithMetadata: Array<NewsImage & ImageMetadata>): ImagePreview[] {
    return imagesWithMetadata.map(img => ({
      file: null,
      url: img.url,
      name: img.storagePath.split('/').pop() ?? 'image',
      size: formatBytes(img.size),
      isExisting: true,
      order: img.order,
      storagePath: img.storagePath,
      uploadedAt: img.uploadedAt,
      ...(img.altText && { altText: img.altText }),
      ...(img.caption && { caption: img.caption })
    }));
  }

  async createImagePreviewsFromFile(files: File[], startOrder: number): Promise<ImagePreview[]> {
    return Promise.all(
      files.map(async (file, index) => ({
        file,
        url: await this.#createImageUrl(file),
        name: file.name,
        size: formatBytes(file.size),
        isExisting: false,
        order: startOrder + index
      }))
    );
  }

  #createImageUrl(file: File): Promise<string> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target?.result as string);
      reader.readAsDataURL(file);
    });
  }

  // Image Upload/Delete

  async uploadImage(newsId: string, file: File, order: number): Promise<NewsImage> {
    const storagePath = this.#getStoragePath(newsId, file.name);

    const metadata: UploadMetadata = {
      contentType: file.type,
      cacheControl: 'public, max-age=31536000, immutable',
      customMetadata: {
        originalName: file.name,
        uploadedBy: 'news-editor',
        newsId
      }
    };

    await uploadBytes(this.#storageRef(storagePath), file, metadata);

    return {
      storagePath,
      order,
      uploadedAt: Timestamp.now()
    };
  }

  async deleteImage(storagePath: string): Promise<void> {
    try {
      await deleteObject(this.#storageRef(storagePath));
      console.log(`Deleted image: ${storagePath}`);
    } catch (error) {
      console.error(`Failed to delete ${storagePath}:`, error);
      throw error;
    }
  }

  async #getImageMetadata(storagePath: string): Promise<ImageMetadata> {
    const storageRef = this.#storageRef(storagePath);
    const [url, metadata] = await Promise.all([
      getDownloadURL(storageRef),
      getMetadata(storageRef)
    ]);

    return {
      url,
      size: metadata.size,
      contentType: metadata.contentType ?? 'image/jpeg'
    };
  }

  async getImagesWithMetadata(images: readonly NewsImage[]): Promise<Array<NewsImage & ImageMetadata>> {
    const sorted = images
      .filter(img => img.storagePath?.trim())
      .sort((a, b) => a.order - b.order); // filter() already returns a copy

    return Promise.all(
      sorted.map(async img => {
        try {
          return { ...img, ...await this.#getImageMetadata(img.storagePath) };
        } catch (error) {
          console.error(`Error loading image ${img.storagePath}:`, error);
          throw error;
        }
      })
    );
  }

  // CRUD Operations

  /** Live list of the current user's company news, newest first. */
  getCompanyNews(): Observable<News[]> {
    return this.#currentUser.company$.pipe(
      switchMap(company => {
        if (!company) return of([]);

        const newsQuery = query(
          this.#collection(),
          where('companyId', '==', company.companyId),
          orderBy('createdAt', 'desc')
        );
        return collectionData(newsQuery, { idField: 'id' }) as Observable<News[]>;
      })
    );
  }

  /** Returns null if the article doesn't exist or belongs to another company. */
  async getNewsById(id: string): Promise<News | null> {
    try {
      const [snapshot, company] = await Promise.all([
        getDoc(this.#docRef(id)),
        this.#currentUser.requireCompany()
      ]);
      if (!snapshot.exists()) return null;

      const news = { id: snapshot.id, ...snapshot.data() } as News;
      return news.companyId === company.companyId ? news : null;
    } catch (error) {
      console.error(`Error fetching news ${id}:`, error);
      return null;
    }
  }

  async createNews(input: CreateNewsInput): Promise<string> {
    const company = await this.#currentUser.requireCompany();

    const docRef = await addDoc(this.#collection(), {
      ...input,
      ...company,
      newsImages: [],
      tags: input.tags ?? [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      publishedAt: input.published ? serverTimestamp() : null,
      viewCount: 0
    });
    return docRef.id;
  }

  async updateNews(id: string, updates: UpdateNewsInput): Promise<void> {
    const data: Record<string, unknown> = {
      ...Object.fromEntries(Object.entries(updates).filter(([, value]) => value !== undefined)),
      updatedAt: serverTimestamp()
    };

    if (updates.published !== undefined) {
      data['publishedAt'] = updates.published ? serverTimestamp() : null;
    }

    await updateDoc(this.#docRef(id), data);
  }

  async deleteNews(id: string): Promise<void> {
    const news = await this.getNewsById(id);
    if (!news) {
      throw new Error(`News ${id} not found`);
    }

    if (news.newsImages.length) {
      await Promise.allSettled(news.newsImages.map(img => this.deleteImage(img.storagePath)));
    }

    await deleteDoc(this.#docRef(id));
  }
}
